import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './index.css';
import { DataProvider } from './lib/DataContext.tsx';
import { ScrollToTop, RedirectOrNotFound } from './routing.tsx';
// The route table lives outside src/ because the Node server reads it too — it is how the server
// tells a real page from an address that should answer 404 rather than the SPA shell. Adding a page
// means adding it there, and both the router and the server pick it up.
import { ALIASES, CMS_PAGES } from '../shared/routes.mjs';

import CmsPage               from './pages/CmsPage.tsx';
import NewsStoryPage         from './pages/NewsStoryPage.tsx';
import ProduktionPage        from './pages/ProduktionPage.tsx';

// Internal component gallery — a developer tool, never meant to ship. Dynamically imported
// behind the DEV flag (rather than statically imported + route-gated) so Vite's dead-code
// elimination drops the import() call itself in production builds: import.meta.env.DEV is
// inlined to `false` there, making this whole expression unreachable, which takes
// PreviewGalleryPage.tsx *and* previewRegistry.tsx (plus its sample images) out of the
// production bundle entirely rather than just out of the router.
const PreviewGalleryPage = import.meta.env.DEV
  ? lazy(() => import('./pages/preview/PreviewGalleryPage.tsx'))
  : null;


createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <DataProvider>
        <ScrollToTop />
        <Routes>
          {/* Every CMS-backed page, in the order routes.mjs lists them. */}
          {CMS_PAGES.map(({ path, slug }) => (
            <Route key={path} path={path} element={<CmsPage slug={slug} />} />
          ))}

          {/* Earlier names for pages that still exist under another address. */}
          {ALIASES.map(({ path, to }) => (
            <Route key={path} path={path} element={<Navigate to={to} replace />} />
          ))}

          <Route path="/nyheder/:id"    element={<NewsStoryPage />} />

          {/* Intern (unlisted – not linked from navigation; listed in routes.mjs as INTERNAL_PAGES) */}
          <Route path="/intern/produktion"     element={<ProduktionPage />} />
          {PreviewGalleryPage && (
            <>
              <Route path="/intern/preview"       element={<Suspense fallback={null}><PreviewGalleryPage /></Suspense>} />
              <Route path="/intern/preview/:slug" element={<Suspense fallback={null}><PreviewGalleryPage /></Suspense>} />
            </>
          )}

          {/* CMS page preview */}
          <Route path="/preview/:guid"       element={<CmsPage preview />} />

          <Route path="*"              element={<RedirectOrNotFound />} />
        </Routes>
      </DataProvider>
    </BrowserRouter>
  </StrictMode>
);
